<template>
  <header class="header">
    <div class="logo">
      <router-link to="/" aria-label="返回首页">
        <img src="../assets/img/logo.png" alt="湖州市工艺美术学会" />
      </router-link>
    </div>

    <button
      class="menu-toggle"
      type="button"
      :aria-expanded="mobileMenuOpen ? 'true' : 'false'"
      aria-controls="site-navigation"
      @click="toggleMobileMenu"
    >
      {{ mobileMenuOpen ? '关闭' : '菜单' }}
    </button>

    <nav id="site-navigation" class="title-bar" :class="{ open: mobileMenuOpen }">
      <div class="titles">
        <div
          v-for="(item, index) in navConfig"
          :key="index"
          class="nav-parent"
          @mouseenter="showSubMenu(index)"
          @mouseleave="delayHideSubMenu"
        >
          <router-link
            v-if="item.link"
            :to="item.link"
            class="nav-link"
            @click="closeMobileMenu"
          >{{ item.title }}</router-link>
          <span v-else class="nav-label">{{ item.title }}</span>

          <div class="submenu-hotzone" v-show="activeSubMenu === index"></div>

          <transition :name="index === 1 || index === 4 ? 'fadeLong' : 'fade'">
            <div
              :class="index === 1 || index === 4 ? 'submenu submenu_long' : 'submenu'"
              v-show="shouldShowSubmenu(index, item)"
              @mouseenter="cancelHide"
              @mouseleave="startHideTimer"
            >
              <div class="submenu-content">
                <a
                  v-for="(subItem, subIndex) in item.sub"
                  :key="subIndex"
                  :href="resolveNavHref('children', subItem.link, item.title)"
                  @click.prevent="handleSubNav(subItem, item.title)"
                >
                  {{ subItem.title }}
                </a>
              </div>
            </div>
          </transition>

          <button
            v-if="isMobile && item.sub && item.sub.length"
            type="button"
            class="mobile-sub-toggle"
            :aria-expanded="mobileExpanded === index ? 'true' : 'false'"
            @click.stop="toggleMobileSub(index)"
          >
            {{ mobileExpanded === index ? '收起' : '展开' }}
          </button>
        </div>
      </div>
    </nav>

    <div v-if="mobileMenuOpen" class="mobile-overlay" @click="closeMobileMenu"></div>
  </header>
</template>

<script>
import { onMounted, onBeforeUnmount, ref } from "vue";
import { useRouter } from "vue-router";
import { getAction } from '@/utils/request';
import { resolveNavHref } from '@/seo/paths';

export default {
  setup() {
    const navConfig = ref([
      {
        title: "学会概述",
        link: "/society/about",
        sub: [
          { title: "学会简介", link: "/society/about" },
          { title: "学会章程", link: "/society/bylaws" },
          { title: "理事会成员", link: "/society/council" },
          { title: "会员中心", link: "/society/members" },
          { title: "工美馆藏", link: "/society/collection" },
        ],
      },
      { title: "行业简介", link: "/industryIntroduction/index", sub: [] },
      {
        title: "大师云集",
        link: "/master/index",
        sub: [
          { link: 10021000, title: '中国工艺美术大师' },
          { link: 10021001, title: '浙江省工艺美术大师' },
          { link: 10021002, title: '湖州市工艺美术大师' },
        ],
      },
      {
        title: "非遗传承",
        link: "/heritage/index",
        sub: [
          { link: 10031000, title: '国家非遗传承人' },
          { link: 10031001, title: '省非遗传承人' },
          { link: 10031002, title: '市非遗传承人' },
          { link: 10031003, title: '区县非遗传承人' },
          { link: 10031004, title: '其他' },
        ],
      },
      { title: "精品荟萃", link: "/highQuality/index", sub: [] },
      { title: "新闻动态", link: "/newsNotice/index" },
      { title: "名家访谈", link: "/interviews/index" },
      { title: "学术研究", link: "/research/index" },
      // { title: "云端集市" },
    ]);

    const activeSubMenu = ref(-1);
    const mobileMenuOpen = ref(false);
    const mobileExpanded = ref(-1);
    const isMobile = ref(false);
    const hideTimer = ref(null);
    const router = useRouter();

    const updateViewport = () => {
      isMobile.value = window.innerWidth <= 1199;
      if (!isMobile.value) {
        mobileMenuOpen.value = false;
        mobileExpanded.value = -1;
      }
    };

    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value;
      document.body.style.overflow = mobileMenuOpen.value ? 'hidden' : '';
      if (!mobileMenuOpen.value) mobileExpanded.value = -1;
    };

    const closeMobileMenu = () => {
      mobileMenuOpen.value = false;
      mobileExpanded.value = -1;
      document.body.style.overflow = '';
    };

    const toggleMobileSub = (index) => {
      mobileExpanded.value = mobileExpanded.value === index ? -1 : index;
    };

    const shouldShowSubmenu = (index, item) => {
      if (!item.sub || !item.sub.length) return false;
      if (isMobile.value) return mobileExpanded.value === index;
      return activeSubMenu.value === index;
    };

    const routerTo = (level, link, title) => {
      if (level === 'children' && title === "精品荟萃") {
        router.push({ name: 'highQuality', params: { msgType: link } });
      } else if (level === "children" && title === "行业简介") {
        router.push({ name: 'industryIntroduction', params: { msgType: link } });
      } else if (level === "children" && title === "大师云集") {
        router.push({ name: 'masterGather', query: { msgType: link } });
      } else if (level === "children" && title === "非遗传承") {
        router.push({ name: 'heritageGather', query: { msgType: link } });
      } else {
        router.push(link);
      }
    };

    const handleSubNav = (subItem, title) => {
      routerTo('children', subItem.link, title);
      closeMobileMenu();
    };

    const showSubMenu = (index) => {
      if (isMobile.value) return;
      clearTimeout(hideTimer.value);
      activeSubMenu.value = index;
    };

    const delayHideSubMenu = () => {
      if (isMobile.value) return;
      hideTimer.value = setTimeout(() => {
        activeSubMenu.value = -1;
      }, 200);
    };

    const cancelHide = () => clearTimeout(hideTimer.value);
    const startHideTimer = () => delayHideSubMenu();

    const getIndustryData = () => {
      getAction('/biz/bizMsgInfo/listAll', { parentType: 1001 }).then(res => {
        if (res.success) {
          const result = res.result;
          navConfig.value[1].sub = result?.map(item => ({
            link: item.msgType,
            title: item.msgTitle,
          })) || [];
          navConfig.value[4].sub = result?.map(item => ({
            link: item.msgType,
            title: item.msgTitle,
          })) || [];
        }
      });
    };

    onMounted(() => {
      updateViewport();
      window.addEventListener('resize', updateViewport);
      getIndustryData();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateViewport);
      document.body.style.overflow = '';
    });

    return {
      navConfig,
      activeSubMenu,
      mobileMenuOpen,
      mobileExpanded,
      isMobile,
      toggleMobileMenu,
      closeMobileMenu,
      toggleMobileSub,
      shouldShowSubmenu,
      routerTo,
      handleSubNav,
      showSubMenu,
      delayHideSubMenu,
      cancelHide,
      startHideTimer,
      resolveNavHref,
    };
  },
};
</script>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 77px;
  background-color: #292929CC;
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  padding-right: 16px;
  box-sizing: border-box;
}

.logo img {
  display: block;
  height: 47px;
  width: clamp(160px, 18vw, 283px);
  margin-left: clamp(16px, 3vw, 45px);
}

.menu-toggle {
  display: none;
  min-width: 64px;
  min-height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: transparent;
  color: #fff;
  border-radius: 6px;
  cursor: pointer;
}

.title-bar {
  display: flex;
  align-items: center;
}

.titles {
  display: flex;
  flex-wrap: wrap;
  gap: clamp(12px, 2vw, 45px);
}

.nav-link,
.nav-label,
.titles span {
  color: #FFFFFF;
  text-decoration: none;
  font-family: "SourceHanSansCN-Regular";
  font-weight: 500;
  font-size: clamp(14px, 1.2vw, 18px);
  line-height: normal;
  cursor: pointer;
}

.nav-link:hover,
.titles span:hover {
  text-decoration: underline;
  text-underline-offset: 10px;
}

.submenu-hotzone {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 30px;
  background: transparent;
  z-index: 999;
}

.submenu {
  z-index: 1000;
  display: flex;
  width: max-content;
  padding: 0 60px;
  left: 50%;
  transform: translateX(-50%);
  position: absolute;
  top: 55px;
  background: #63513e;
  backdrop-filter: blur(3px);
  opacity: 0.95;
  line-height: 57px;
  color: #fdf7ea;
}

.submenu_long {
  position: fixed;
  left: 0;
  top: 80px;
  transform: translateX(0);
  width: 100vw;
  box-sizing: border-box;
}

.submenu-content {
  width: 100%;
  max-width: calc(100vw);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.submenu a {
  margin-right: 60px;
  padding-left: 45px;
  box-sizing: border-box;
  cursor: pointer;
  color: inherit;
  text-decoration: none;
}

.nav-parent {
  position: relative;
  display: flex;
  align-items: center;
}

.mobile-sub-toggle {
  display: none;
  margin-left: 8px;
  min-height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  border-radius: 4px;
}

.mobile-overlay {
  display: none;
}

.fade-enter-active,
.fade-leave-active,
.fadeLong-enter-active,
.fadeLong-leave-active {
  transition: all 0.3s;
}

.fade-enter-from,
.fade-leave-to,
.fadeLong-enter-from,
.fadeLong-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

@media (max-width: 1199px) {
  .header {
    justify-content: center;
    position: relative;
    height: 60px;
    padding-left: 16px;
    padding-right: 16px;
  }

  .logo {
    display: flex;
    justify-content: center;
    flex: 1;
  }

  .logo img {
    margin-left: 0;
    margin-right: 0;
  }

  .menu-toggle {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .title-bar {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    width: 100%;
    max-height: calc(100vh - 60px);
    background:
      linear-gradient(180deg, rgba(65, 63, 57, 0.98) 0%, rgba(35, 35, 33, 0.98) 100%);
    transform: translateY(-12px);
    opacity: 0;
    visibility: hidden;
    transition: transform 0.22s ease, opacity 0.22s ease, visibility 0.22s ease;
    overflow-y: auto;
    padding: 12px 18px 18px;
    box-sizing: border-box;
    z-index: 1001;
    box-shadow: 0 18px 36px rgba(0, 0, 0, 0.32);
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  .title-bar.open {
    transform: translateY(0);
    opacity: 1;
    visibility: visible;
  }

  .titles {
    display: grid;
    grid-template-columns: 1fr;
    align-items: stretch;
    gap: 0;
    width: 100%;
  }

  .nav-parent {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    min-height: 50px;
    padding: 0;
  }

  .nav-link,
  .nav-label,
  .titles span {
    display: flex;
    align-items: center;
    min-height: 50px;
    font-size: 18px;
    line-height: 1.2;
    letter-spacing: 0;
  }

  .nav-link:hover,
  .titles span:hover {
    text-decoration: none;
  }

  .mobile-sub-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 52px;
    min-height: 32px;
    margin-left: 12px;
    padding: 0 12px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
    border-radius: 999px;
    font-size: 14px;
  }

  .submenu,
  .submenu_long {
    grid-column: 1 / -1;
    position: static;
    transform: none;
    width: 100%;
    padding: 6px;
    margin: 0 0 12px;
    line-height: 1.6;
    background: rgba(255, 255, 255, 0.07);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.9);
  }

  .submenu-content {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px 8px;
    align-items: start;
  }

  .submenu a {
    margin: 0;
    padding: 9px 8px;
    font-size: 14px;
    line-height: 1.45;
    border-radius: 6px;
    color: inherit;
  }

  .submenu-hotzone {
    display: none;
  }

  .mobile-overlay {
    display: block;
    position: fixed;
    inset: 60px 0 0 0;
    background: rgba(0, 0, 0, 0.35);
    z-index: 999;
  }
}
</style>
